// A Signal template built from a mapping: every distinct Signal name the
// mapping produces on the example (or, with no example values, the names its
// rules spell out) becomes a preset, minus the ones the destination channel's
// templates already offer.
import type { PreviewRecordResult } from './mappingPreview';
import type { MappingSpec } from './mappingEngine';
import { getOutputs } from './mappingSpecEdit';
import type { Template } from '../types';

export const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const MAX_SIGNALS = 30;

export function producedSignalNames(spec: MappingSpec | null, results: PreviewRecordResult[] | null): string[] {
  const names = new Set<string>();
  for (const r of results ?? []) for (const o of r.outputs) if (o.signal?.name) names.add(o.signal.name);
  if (spec && names.size === 0) {
    for (const rules of getOutputs(spec)) {
      for (const rule of rules) {
        if (rule.to !== 'name') continue;
        if (typeof rule.const === 'string' && rule.const) names.add(rule.const);
        for (const v of Object.values(rule.map ?? {})) if (typeof v === 'string' && v) names.add(v);
      }
    }
  }
  return [...names].slice(0, MAX_SIGNALS);
}

export function newSignalNames(produced: string[], existing: string[]): string[] {
  const have = new Set(existing.map((n) => n.toLowerCase()));
  return produced.filter((n) => !have.has(n.toLowerCase()));
}

// A signal entry with its own emoji (e.g. host metrics, where "cpu_usage_percent"
// and "mem_used_percent" read better with distinct icons than one shared "⚡️").
export interface NamedSignal {
  name: string;
  emoji?: string;
}

export function buildSignalTemplateEntries(title: string, nickname: string, entries: NamedSignal[], newId: () => string, ownerId: string): Template {
  return {
    id: newId(),
    title,
    nickname,
    isPublic: false,
    colorHex: COLORS[0],
    emoji: '✨',
    avatarImageKey: '',
    signals: entries.map((e, i) => ({ id: newId(), name: e.name, colorHex: COLORS[i % COLORS.length], emoji: e.emoji ?? '⚡️' })),
    likes: 0,
    liked: false,
    usages: 0,
    ownerId,
  };
}

export function buildSignalTemplate(title: string, nickname: string, names: string[], newId: () => string, ownerId: string): Template {
  return buildSignalTemplateEntries(title, nickname, names.map((name) => ({ name })), newId, ownerId);
}
